import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SearchableSelect from '../../components/common/SearchableSelect';
import { usePaginacao } from '../../hooks/usePaginacao';
import { useSortable } from '../../hooks/useSortable';
import { useAuth } from '../../contexts/AuthContext';
import * as api from '../../api/tipos';
import * as coresApi from '../../api/cores';
import * as tamanhosApi from '../../api/tamanhos';
import * as empresasApi from '../../api/empresas';

function Modal({ item, cores, tamanhos, empresas, emGlobal, onClose, onSaved }) {
  const editando = !!item?.id_tipo_uniforme;
  const [nome, setNome] = useState(item?.nome || '');
  const [idEmpresa, setIdEmpresa] = useState('');
  const [tamanhosSel, setTamanhosSel] = useState(item?.tamanhos_padrao?.map(t => t.id_tamanho_uniforme) || []);
  const [coresSel, setCoresSel] = useState(item?.cores_padrao?.map(c => c.id_cor) || []);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  const precisaEmpresa = !editando && emGlobal;
  const empresaOpts = empresas.map(e => ({ value: e.id_empresa_operadora, label: e.nome }));
  const empresaAlvo = editando ? item.id_empresa_operadora : idEmpresa;
  // No Modo Global os tamanhos/cores vem de todas as empresas misturados — filtra
  // pela empresa alvo deste tipo, senão a grade sugerida mistura dados de outras.
  const tamanhosDaEmpresa = emGlobal ? tamanhos.filter(t => t.id_empresa_operadora === empresaAlvo) : tamanhos;
  const coresDaEmpresa = emGlobal ? cores.filter(c => c.id_empresa_operadora === empresaAlvo) : cores;

  const toggleTam = (id) => setTamanhosSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleCor = (id) => setCoresSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const salvar = async (e) => {
    e.preventDefault();
    if (precisaEmpresa && !idEmpresa) {
      setErro('Selecione a empresa dona deste tipo.');
      return;
    }
    setErro('');
    setLoading(true);
    const payload = {
      nome,
      ...(precisaEmpresa ? { id_empresa_operadora: idEmpresa } : {}),
      tamanhos_padrao: tamanhosSel.map(t => ({ id_tamanho_uniforme: t })),
      cores_padrao: coresSel,
    };
    try {
      if (editando) await api.atualizar(item.id_tipo_uniforme, payload);
      else await api.criar(payload);
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      setErro(typeof d === 'string' ? d : 'Erro ao salvar.');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title mb-0">{editando ? 'Editar Tipo' : 'Novo Tipo'}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={salvar}>
            <div className="modal-body">
              {erro && <div className="alert alert-danger py-2">{erro}</div>}

              {precisaEmpresa && (
                <div className="mb-3">
                  <label className="form-label fw-semibold">Empresa *</label>
                  <SearchableSelect options={empresaOpts} value={idEmpresa}
                    onChange={setIdEmpresa} placeholder="Selecione a empresa dona deste tipo..." required />
                  <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                    Você está no modo global — escolha para qual empresa este tipo será cadastrado.
                  </div>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label fw-semibold">Nome *</label>
                <input className="form-control" value={nome} onChange={e => setNome(e.target.value)} required autoFocus />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Grade de tamanhos sugerida</label>
                <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                  Usada como sugestão ao cadastrar um modelo novo deste tipo — não afeta modelos já cadastrados.
                </div>
                {tamanhosDaEmpresa.length === 0 ? (
                  <div className="text-muted" style={{ fontSize: 13 }}>Nenhum tamanho cadastrado ainda — cadastre em "Tamanhos".</div>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {tamanhosDaEmpresa.map(t => (
                      <button key={t.id_tamanho_uniforme} type="button"
                        className={`btn btn-sm ${tamanhosSel.includes(t.id_tamanho_uniforme) ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={{ minWidth: 44 }} onClick={() => toggleTam(t.id_tamanho_uniforme)}>
                        {t.nome}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="form-label fw-semibold">Cores sugeridas</label>
                {coresDaEmpresa.length === 0 ? (
                  <div className="text-muted" style={{ fontSize: 13 }}>Nenhuma cor cadastrada ainda — cadastre em "Cores".</div>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {coresDaEmpresa.map(c => (
                      <button key={c.id_cor} type="button"
                        className={`btn btn-sm ${coresSel.includes(c.id_cor) ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => toggleCor(c.id_cor)}>
                        {c.codigo_hex && (
                          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: c.codigo_hex, marginRight: 6 }} />
                        )}
                        {c.nome}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm" /> : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function Tipos() {
  const { user } = useAuth();
  const souAdmin = user?.perfil === 'admin';
  const emGlobal = souAdmin && !user?.empresa_ativa_id;

  const [items, setItems] = useState([]);
  const [cores, setCores] = useState([]);
  const [tamanhos, setTamanhos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [idEmpresaFiltro, setIdEmpresaFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [modal, setModal] = useState(null);

  const carregar = useCallback(() => {
    setLoading(true);
    Promise.all([api.listar(), coresApi.listar(), tamanhosApi.listar()])
      .then(([t, c, tam]) => { setItems(t.data); setCores(c.data); setTamanhos(tam.data); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (souAdmin) empresasApi.listEmpresas().then(r => setEmpresas(r.data)).catch(() => {});
  }, [souAdmin]);

  const empresaNome = id => empresas.find(e => e.id_empresa_operadora === id)?.nome || '—';

  const filtrados = items.filter(i => {
    const matchBusca = i.nome.toLowerCase().includes(busca.toLowerCase());
    const matchEmpresa = emGlobal && idEmpresaFiltro ? i.id_empresa_operadora === idEmpresaFiltro : true;
    return matchBusca && matchEmpresa;
  });
  const { sorted, Th } = useSortable(filtrados, 'nome');
  const { paginados, Paginacao } = usePaginacao(sorted);

  if (loading) return <LoadingSpinner />;

  const empresaFiltroOpts = [{ value: '', label: 'Todas as empresas' },
    ...empresas.map(e => ({ value: e.id_empresa_operadora, label: e.nome }))];

  return (
    <div>
      <PageHeader titulo="Tipos de Uniforme" subtitulo={`${items.length} tipos`}>
        <button className="btn btn-primary btn-sm" onClick={() => setModal({})}>+ Novo Tipo</button>
      </PageHeader>

      <div className="d-flex gap-2 mb-3 flex-wrap">
        <input className="form-control" style={{ maxWidth: 360 }}
          placeholder="Buscar..." value={busca} onChange={e => setBusca(e.target.value)} />
        {emGlobal && (
          <select className="form-select" style={{ maxWidth: 220 }}
            value={idEmpresaFiltro} onChange={e => setIdEmpresaFiltro(e.target.value)}>
            {empresaFiltroOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}
      </div>

      <div className="card">
        <div className="card-body p-0">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <Th campo="nome">Nome</Th>
                {emGlobal && !idEmpresaFiltro && <th>Empresa</th>}
                <th>Grade padrão</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {paginados.length === 0 && (
                <tr><td colSpan={emGlobal && !idEmpresaFiltro ? 4 : 3} className="text-center text-muted py-4">Nenhum tipo encontrado.</td></tr>
              )}
              {paginados.map(t => (
                <tr key={t.id_tipo_uniforme}>
                  <td className="fw-semibold">{t.nome}</td>
                  {emGlobal && !idEmpresaFiltro && <td>{empresaNome(t.id_empresa_operadora)}</td>}
                  <td className="text-muted" style={{ fontSize: 13 }}>
                    {(t.tamanhos_padrao || []).length} tamanho(s) · {(t.cores_padrao || []).length} cor(es)
                  </td>
                  <td>
                    <button className="btn btn-sm btn-outline-primary py-0 px-2"
                      onClick={() => setModal(t)}>Editar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Paginacao />

      {modal !== null && (
        <Modal item={modal} cores={cores} tamanhos={tamanhos} empresas={empresas} emGlobal={emGlobal}
          onClose={() => setModal(null)} onSaved={() => { setModal(null); carregar(); }} />
      )}
    </div>
  );
}
